import React from 'react';
import PropTypes from 'prop-types';
import { map } from 'lodash';
import { LinearProgress } from '@material-ui/core';
import InfiniteScroll from 'components/InfiniteScroll';
import Message from './Message';

const styles = {
  loading: {
    wrapper: {
      display: 'inline-block',
      flex: 1,
      padding: '20% 40% 0%',
      textAlign: 'center',
      width: '100%',
    },
    paginationWrapper: {
      display: 'inline-block',
      flex: 1,
      padding: '24px 40% 0%',
      textAlign: 'center',
      width: '100%',
    },
  },
  wrapper: {
    flex: '1 0 0',
    position: 'relative',
    overflowY: 'auto',
    padding: 20,
  },
};

class MessagesArea extends React.Component {
  componentDidUpdate(prevProps) {
    const { messages, newMessage, currentPage } = this.props;
    if (!newMessage && (!messages || !messages.length)) return;

    const newMessageEntered = newMessage && prevProps.messages.length !== messages.length;

    if (!currentPage || !prevProps.messages || newMessageEntered) {
      // Scroll to latest or most recent message
      const behavior = newMessageEntered ? 'smooth' : 'instant';
      if (this.bottomMessage) this.bottomMessage.scrollIntoView({ behavior });
    } else if (messages.length > prevProps.messages.length && currentPage > 1) {
      // Scrol to first message from previous loaded page
      // FIXME depending how long takes to render new messages on the top it doesn't keep the scroll
      if (this.firstMessagePreviousPage) {
        this.firstMessagePreviousPage.scrollIntoView({ behavior: 'instant' });
      }
    }
  }

  renderMessage(message) {
    const { currentUser, patient, permissions } = this.props;
    let interlocutor = !('createdBy' in message) ? currentUser : message.createdBy;

    if (typeof interlocutor === 'string') interlocutor = currentUser;

    if (message.type === 'inbound') interlocutor = patient;

    return (
      <Message
        currentUser={currentUser}
        // FIXME propTypes for patient are allowing an empty `interlocutor` at this point
        interlocutor={interlocutor || patient}
        permissions={permissions}
        message={message}
      />
    );
  }

  renderMessages() {
    const { hasMoreMessages, messages, messagesPerPage, onLoadMoreMessages, currentPage } = this.props;

    return (
      <InfiniteScroll
        loadMore={onLoadMoreMessages}
        hasMore={hasMoreMessages}
        useWindow={false}
        initialLoad={false}
        isReverse
        threshold={250}
      >
        {map(messages, (message, index) => {
          const firstFromLatest =
            messagesPerPage - (messagesPerPage * currentPage - messages.length) || messages.length / currentPage;

          if (index === firstFromLatest) {
            // first of the latest previous messages loaded page
            return (
              <div
                key={index}
                ref={(div) => {
                  this.firstMessagePreviousPage = div;
                }}
              >
                {this.renderMessage(message)}
              </div>
            );
          }
          if (index === messages.length - 1) {
            // Most recent or latest message.
            return (
              <div
                key={index}
                ref={(div) => {
                  this.bottomMessage = div;
                }}
              >
                {this.renderMessage(message)}
              </div>
            );
          }

          return <div key={index}>{this.renderMessage(message)}</div>;
        })}
      </InfiniteScroll>
    );
  }

  render() {
    const { currentPage, loading, messages, patient } = this.props;

    if (loading && currentPage === 1 && patient && !messages) {
      return (
        <div style={styles.loading.wrapper}>
          Loading...
          <LinearProgress variant="query" />
        </div>
      );
    }

    return (
      <div style={styles.wrapper}>
        {loading && (
          <div style={styles.loading.paginationWrapper}>
            Loading...
            <LinearProgress variant="query" />
          </div>
        )}
        {this.renderMessages()}
      </div>
    );
  }
}

MessagesArea.propTypes = {
  currentPage: PropTypes.number,
  currentUser: PropTypes.object.isRequired,
  hasMoreMessages: PropTypes.bool,
  loading: PropTypes.bool,
  messages: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  messagesPerPage: PropTypes.number,
  newMessage: PropTypes.bool,
  onLoadMoreMessages: PropTypes.func,
  patient: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  permissions: PropTypes.object.isRequired,
};

MessagesArea.defaultTypes = {
  messages: false,
  patient: false,
};

export default MessagesArea;
